import type { ViewState } from 'react-map-gl/mapbox';

// In-memory stash of the map camera for the map -> Layer Library -> back round-trip.
// The Library is a full-page route, so the source map unmounts and remounts on its
// default fit. Opening the Library saves the camera keyed by the source map and its
// history entry; the returning map consumes it one-shot. Restoring requires mounting
// on the saved history entry, which only a back navigation (in-app arrow or browser
// back) lands on, so any pushed navigation elsewhere leaves the stash inert.

type ReturnViewportStash = { key: string; historyKey: string; viewState: ViewState };

let stash: ReturnViewportStash | null = null;

/** Key for the map surface the Library was opened from. */
export function layerLibraryReturnKey(projectId?: string) {
  return projectId ? `project:${projectId}` : 'home';
}

// The stash keys restores to the history ENTRY it was saved on. `__TSR_key` is unique per
// entry (a replace keeps `__TSR_index` but assigns a new key, and a pushed entry can reuse
// a skipped-over entry's index, so the index can collide across distinct entries). It is
// typed but `__`-prefixed internal TanStack Router state; every read goes through the two
// accessors below so a router upgrade has one fix point.

type HistoryEntryState = { location: { state: { __TSR_key?: string } } };

/**
 * The current history entry key. Components pass this to `useRouterState({ select })` for a
 * subscribed read; event handlers call it directly on `router.history` for a fresh one.
 */
export function historyEntryKeySelector(state: HistoryEntryState): string | undefined {
  return state.location.state.__TSR_key;
}

/** Capture the camera and history entry of the map being left for the Layer Library. */
export function saveReturnViewport(key: string, historyKey: string | undefined, viewState: ViewState) {
  // No entry key (pre-hydration edge) means the restore could never match; don't stash.
  if (!historyKey) return;
  stash = { key, historyKey, viewState };
}

/** Read without consuming (safe at render time, e.g. for `initialViewState`). */
export function peekReturnViewport(key: string, historyKey: string | undefined): ViewState | undefined {
  return historyKey && stash?.key === key && stash.historyKey === historyKey ? stash.viewState : undefined;
}

/** One-shot consume: returns the camera when the key and history entry match, then clears. */
export function consumeReturnViewport(key: string, historyKey: string | undefined): ViewState | undefined {
  const viewState = peekReturnViewport(key, historyKey);
  if (viewState) {
    stash = null;
  }
  return viewState;
}

export function clearReturnViewport() {
  stash = null;
}
